"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, useReducedMotion } from "motion/react";
import { useState } from "react";

export interface EmptyTombProps {
  className?: string;
  compact?: boolean;
}

// Spring animation config for premium feel
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 120,
};

const softSpring = {
  type: "spring" as const,
  damping: 40,
  stiffness: 90,
};

// Main Empty Tomb Scene Component
export function EmptyTomb({ className, compact = false }: EmptyTombProps) {
  const prefersReducedMotion = useReducedMotion();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className={cn(
        "relative w-full overflow-hidden rounded-2xl",
        compact ? "h-[300px] md:h-[380px]" : "h-[380px] md:h-[500px]",
        className
      )}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <svg
        viewBox="0 0 800 500"
        className="w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          {/* Sky gradient - soft dawn colors */}
          <linearGradient id="skyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a8c8e8" />
            <stop offset="40%" stopColor="#d4e4f0" />
            <stop offset="70%" stopColor="#f5e6d3" />
            <stop offset="100%" stopColor="#ffecd2" />
          </linearGradient>

          {/* Cliff face gradient - warm stone */}
          <linearGradient id="cliffGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c4b5a0" />
            <stop offset="50%" stopColor="#a89580" />
            <stop offset="100%" stopColor="#8a7860" />
          </linearGradient>

          {/* Cliff shadow areas */}
          <linearGradient id="cliffShadow" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7a6850" />
            <stop offset="100%" stopColor="#8a7860" />
          </linearGradient>

          {/* Tomb interior - dark with glow */}
          <radialGradient id="tombInterior" cx="50%" cy="60%" r="80%">
            <stop offset="0%" stopColor="#3a3530" />
            <stop offset="60%" stopColor="#252220" />
            <stop offset="100%" stopColor="#1a1815" />
          </radialGradient>

          {/* Divine glow from within */}
          <radialGradient id="divineGlow" cx="50%" cy="70%" r="60%">
            <stop offset="0%" stopColor="rgba(255, 248, 235, 0.4)" />
            <stop offset="40%" stopColor="rgba(255, 240, 220, 0.2)" />
            <stop offset="100%" stopColor="rgba(255, 235, 210, 0)" />
          </radialGradient>

          {/* Stone gradient */}
          <radialGradient id="stoneGradient" cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#c4b5a0" />
            <stop offset="50%" stopColor="#a89580" />
            <stop offset="100%" stopColor="#8a7860" />
          </radialGradient>

          {/* Ground gradient */}
          <linearGradient id="groundGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8fa870" />
            <stop offset="50%" stopColor="#7a9560" />
            <stop offset="100%" stopColor="#6a8550" />
          </linearGradient>

          {/* Path/dirt gradient */}
          <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#b8a890" />
            <stop offset="100%" stopColor="#a09078" />
          </linearGradient>

          {/* Linen cloth gradient */}
          <linearGradient id="linenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#faf8f5" />
            <stop offset="50%" stopColor="#f0ebe5" />
            <stop offset="100%" stopColor="#e8e0d8" />
          </linearGradient>

          {/* Tree gradient */}
          <linearGradient id="treeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#5a7048" />
            <stop offset="100%" stopColor="#4a6038" />
          </linearGradient>

          {/* Light beam filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Soft shadow filter */}
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="4" />
            <feOffset dx="2" dy="4" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* === SKY === */}
        <motion.rect
          x="0"
          y="0"
          width="800"
          height="500"
          fill="url(#skyGradient)"
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        />

        {/* === DISTANT HILLS === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...softSpring, delay: 0.1 }}
        >
          {/* Far hill */}
          <path
            d="M0 180 Q150 120 300 160 Q450 100 600 140 Q700 110 800 150 L800 280 L0 280 Z"
            fill="#c8b8a0"
            opacity="0.6"
          />
          {/* Mid hill with village silhouette */}
          <path
            d="M500 140 Q550 130 600 145 Q650 125 700 140 L700 180 L500 180 Z"
            fill="#b8a890"
            opacity="0.7"
          />
          {/* Simple village buildings */}
          <g fill="#c4b4a0" opacity="0.8">
            <rect x="530" y="130" width="25" height="20" />
            <rect x="560" y="125" width="30" height="25" />
            <rect x="600" y="128" width="20" height="22" />
            <rect x="630" y="132" width="28" height="18" />
          </g>
        </motion.g>

        {/* === MAIN CLIFF FACE === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...softSpring, delay: 0.2 }}
        >
          {/* Main rock mass */}
          <path
            d="M50 500
               L30 400
               Q20 300 60 220
               Q100 160 180 130
               Q280 100 400 95
               Q520 100 620 130
               Q700 160 740 220
               Q780 300 770 400
               L750 500 Z"
            fill="url(#cliffGradient)"
          />

          {/* Rock shadow/depth layers */}
          <path
            d="M100 500 L90 420 Q85 350 120 280 Q160 220 250 190 L250 500 Z"
            fill="#9a8870"
            opacity="0.5"
          />
          <path
            d="M550 500 L700 500 Q720 400 680 300 Q650 230 550 200 L550 500 Z"
            fill="#9a8870"
            opacity="0.4"
          />

          {/* Rock texture lines */}
          <g stroke="#8a7860" strokeWidth="1.5" opacity="0.3" fill="none">
            <path d="M120 350 Q200 340 280 360" />
            <path d="M100 400 Q180 385 260 405" />
            <path d="M540 350 Q620 340 680 360" />
            <path d="M560 410 Q630 395 700 415" />
            <path d="M200 250 Q300 230 380 260" />
            <path d="M420 250 Q500 235 580 260" />
          </g>

          {/* Highlight on cliff top */}
          <path
            d="M180 130 Q280 100 400 95 Q520 100 620 130"
            stroke="#d4c4b0"
            strokeWidth="4"
            fill="none"
            opacity="0.5"
          />
        </motion.g>

        {/* === TOMB ENTRANCE (DARK INTERIOR) === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...softSpring, delay: 0.4 }}
        >
          {/* Entrance shadow/depth */}
          <ellipse cx="340" cy="340" rx="105" ry="130" fill="#1a1815" />

          {/* Main entrance opening */}
          <ellipse cx="340" cy="335" rx="95" ry="120" fill="url(#tombInterior)" />

          {/* Interior glow - pulsing */}
          <motion.ellipse
            cx="340"
            cy="340"
            rx="90"
            ry="115"
            fill="url(#divineGlow)"
            animate={
              prefersReducedMotion
                ? {}
                : {
                  opacity: isHovered ? [0.6, 0.9, 0.6] : [0.4, 0.7, 0.4],
                }
            }
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Light rays emanating outward */}
          <motion.g
            animate={
              prefersReducedMotion
                ? {}
                : {
                  opacity: isHovered ? [0.15, 0.25, 0.15] : [0.08, 0.15, 0.08],
                }
            }
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <path
              d="M340 340 L280 200 L300 200 L340 320 Z"
              fill="rgba(255, 248, 235, 0.3)"
            />
            <path
              d="M340 340 L320 190 L350 190 L340 320 Z"
              fill="rgba(255, 248, 235, 0.4)"
            />
            <path
              d="M340 340 L360 195 L390 200 L340 320 Z"
              fill="rgba(255, 248, 235, 0.3)"
            />
          </motion.g>

          {/* Carved entrance rim */}
          <ellipse
            cx="340"
            cy="338"
            rx="100"
            ry="125"
            fill="none"
            stroke="#7a6850"
            strokeWidth="12"
          />
          <ellipse
            cx="340"
            cy="340"
            rx="108"
            ry="133"
            fill="none"
            stroke="#6a5840"
            strokeWidth="4"
          />
        </motion.g>

        {/* === BURIAL BENCH INSIDE === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...softSpring, delay: 0.6 }}
        >
          {/* Stone bench - visible through entrance */}
          <path
            d="M280 400 L290 380 L390 380 L400 400 L400 420 L280 420 Z"
            fill="#4a4540"
          />
          <path
            d="M290 380 L390 380 L400 400 L280 400 Z"
            fill="#5a5550"
          />
        </motion.g>

        {/* === FOLDED LINEN CLOTHS === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...softSpring, delay: 0.8 }}
        >
          {/* Main folded body cloth - neatly folded stack */}
          <motion.g
            animate={
              prefersReducedMotion
                ? {}
                : {
                  scale: isHovered ? 1.05 : 1,
                }
            }
            transition={springConfig}
            style={{ transformOrigin: "340px 390px" }}
          >
            {/* Bottom layer */}
            <rect
              x="305"
              y="385"
              width="70"
              height="12"
              rx="2"
              fill="#e8e0d8"
            />
            {/* Middle layer */}
            <rect
              x="310"
              y="378"
              width="60"
              height="10"
              rx="2"
              fill="#f0ebe5"
            />
            {/* Top layer */}
            <rect
              x="315"
              y="372"
              width="50"
              height="8"
              rx="2"
              fill="url(#linenGradient)"
            />

            {/* Fold lines on top layer */}
            <g stroke="#d5cdc5" strokeWidth="1" opacity="0.6">
              <line x1="320" y1="375" x2="360" y2="375" />
              <line x1="318" y1="378" x2="362" y2="378" />
            </g>

            {/* Cloth glow */}
            <motion.rect
              x="300"
              y="368"
              width="80"
              height="35"
              rx="4"
              fill="rgba(255, 248, 235, 0.15)"
              animate={
                prefersReducedMotion
                  ? {}
                  : {
                    opacity: isHovered ? [0.2, 0.35, 0.2] : [0.1, 0.2, 0.1],
                  }
              }
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.g>

          {/* Head cloth - rolled separately (John 20:7) */}
          <motion.g
            animate={
              prefersReducedMotion
                ? {}
                : {
                  scale: isHovered ? 1.08 : 1,
                }
            }
            transition={springConfig}
            style={{ transformOrigin: "395px 385px" }}
          >
            {/* Rolled cloth */}
            <ellipse cx="395" cy="388" rx="18" ry="10" fill="#e8e0d8" />
            <ellipse cx="395" cy="386" rx="14" ry="7" fill="#f0ebe5" />
            <ellipse cx="395" cy="384" rx="10" ry="4" fill="#faf8f5" />

            {/* Roll spiral lines */}
            <path
              d="M383 387 Q390 382 395 382 Q400 382 407 387"
              stroke="#d5cdc5"
              strokeWidth="1.5"
              fill="none"
            />
          </motion.g>
        </motion.g>

        {/* === THE ROLLED STONE === */}
        <motion.g
          initial={
            prefersReducedMotion
              ? {}
              : { opacity: 0, x: -40, rotate: -15 }
          }
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ ...softSpring, delay: 0.5, duration: 1.5 }}
          filter="url(#softShadow)"
        >
          {/* Stone shadow on ground */}
          <ellipse
            cx="540"
            cy="460"
            rx="90"
            ry="25"
            fill="rgba(0,0,0,0.2)"
          />

          {/* Main stone disc */}
          <circle cx="520" cy="360" r="100" fill="url(#stoneGradient)" />

          {/* Stone texture - concentric rings showing it's a disc */}
          <circle
            cx="520"
            cy="360"
            r="85"
            fill="none"
            stroke="#9a8a78"
            strokeWidth="2"
            opacity="0.3"
          />
          <circle
            cx="520"
            cy="360"
            r="65"
            fill="none"
            stroke="#9a8a78"
            strokeWidth="1.5"
            opacity="0.25"
          />
          <circle
            cx="520"
            cy="360"
            r="45"
            fill="none"
            stroke="#9a8a78"
            strokeWidth="1"
            opacity="0.2"
          />

          {/* Stone surface texture */}
          <g stroke="#8a7a68" strokeWidth="1.5" opacity="0.3" fill="none">
            <path d="M460 340 Q490 330 520 345" />
            <path d="M470 370 Q510 360 550 375" />
            <path d="M455 400 Q500 390 545 405" />
            <path d="M500 310 Q530 305 560 320" />
          </g>

          {/* Stone highlight */}
          <path
            d="M450 310 Q490 280 550 290 Q600 305 610 340"
            stroke="#d4c4b0"
            strokeWidth="5"
            fill="none"
            opacity="0.4"
          />

          {/* Stone thickness edge (showing it's a disc) */}
          <path
            d="M420 360 Q420 450 520 460 Q620 450 620 360"
            fill="#8a7a68"
            opacity="0.6"
          />
        </motion.g>

        {/* === STONE TRACK/GROOVE === */}
        <motion.path
          d="M250 450 Q350 445 450 448 Q520 450 560 455"
          stroke="#9a8a78"
          strokeWidth="8"
          fill="none"
          opacity="0.4"
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ ...softSpring, delay: 0.3 }}
        />

        {/* === FOREGROUND GROUND === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...softSpring, delay: 0.15 }}
        >
          {/* Main grass ground */}
          <path
            d="M0 420 Q200 400 400 415 Q600 400 800 420 L800 500 L0 500 Z"
            fill="url(#groundGradient)"
          />

          {/* Dirt path leading to tomb */}
          <path
            d="M300 500 Q340 460 360 445 Q380 435 400 440 Q450 450 500 500"
            fill="url(#pathGradient)"
            opacity="0.7"
          />

          {/* Grass texture lines */}
          <g stroke="#5a7548" strokeWidth="2" opacity="0.4">
            <path d="M50 450 L55 435 L60 450" />
            <path d="M80 455 L87 438 L94 455" />
            <path d="M120 448 L125 432 L130 448" />
            <path d="M700 445 L707 428 L714 445" />
            <path d="M730 452 L735 438 L740 452" />
            <path d="M760 448 L767 432 L774 448" />
          </g>

          {/* Small rocks on ground */}
          <g fill="#a09080" opacity="0.6">
            <ellipse cx="150" cy="465" rx="12" ry="6" />
            <ellipse cx="650" cy="458" rx="10" ry="5" />
            <ellipse cx="250" cy="480" rx="8" ry="4" />
          </g>
        </motion.g>

        {/* === CYPRESS TREES === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...softSpring, delay: 0.25 }}
        >
          {/* Left tree */}
          <path
            d="M80 480 Q70 400 75 350 Q78 300 82 280 Q85 300 88 350 Q93 400 83 480 Z"
            fill="url(#treeGradient)"
          />
          <path
            d="M80 480 Q72 420 77 380 Q80 350 82 340"
            stroke="#4a5838"
            strokeWidth="2"
            fill="none"
            opacity="0.5"
          />

          {/* Right tree 1 */}
          <path
            d="M700 470 Q688 380 693 320 Q697 270 702 250 Q707 270 711 320 Q716 380 704 470 Z"
            fill="url(#treeGradient)"
          />

          {/* Right tree 2 */}
          <path
            d="M750 485 Q742 420 746 380 Q749 340 752 325 Q755 340 758 380 Q762 420 754 485 Z"
            fill="#5a6848"
          />
        </motion.g>

        {/* === SMALL VEGETATION === */}
        <motion.g
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...softSpring, delay: 0.35 }}
        >
          {/* Small bushes near cliff */}
          <ellipse cx="180" cy="420" rx="25" ry="15" fill="#6a8050" opacity="0.8" />
          <ellipse cx="620" cy="415" rx="30" ry="18" fill="#5a7040" opacity="0.7" />

          {/* Small plants on cliff */}
          <path d="M150 280 Q155 260 160 280" stroke="#6a8050" strokeWidth="3" fill="none" />
          <path d="M155 275 Q162 255 168 278" stroke="#5a7040" strokeWidth="2" fill="none" />
        </motion.g>
      </svg>
    </motion.div>
  );
}
